import React, { useMemo } from "react";
import { CLIENT_LOGOS } from "@/data/clients";
import { SITE } from "@/constants/testIds";

/**
 * Trusted-by section — an authentic 35mm film strip. Every client mark is
 * rendered as a uniform white silhouette (Relatable brand white) sitting
 * directly on the dark film base — no matting cards. Punched rounded-rect
 * perforations run top and bottom, and each frame carries a fog
 * edge-print credit + frame number like real exposed stock.
 *
 * Auto-splits into two counter-scrolling rows above 14 unique logos.
 * Hover pauses the strip.
 */
export default function TrustedBy({ items = CLIENT_LOGOS }) {
  const unique = useMemo(() => {
    const seen = new Set();
    return items.filter((it) => {
      if (!it?.name || !it?.logo) return false;
      const k = it.name.trim().toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [items]);

  if (unique.length === 0) return null;

  const useTwoRows = unique.length > 14;
  const half = Math.ceil(unique.length / 2);
  const rowA = useTwoRows ? unique.slice(0, half) : unique;
  const rowB = useTwoRows ? unique.slice(half) : [];

  return (
    <section
      data-testid={SITE.trustedBySection || "trusted-by-section"}
      className="relative bg-ink text-bone py-16 md:py-20 border-t border-b border-line overflow-hidden"
      aria-label="Trusted by"
    >
      {/* Editorial header */}
      <div className="px-6 md:px-12 lg:px-24 mb-10 md:mb-14 flex items-end justify-between gap-6">
        <p className="font-display text-3xl md:text-5xl tracking-tighter font-medium text-bone leading-[0.98] max-w-3xl">
          Brands we&apos;ve made <span className="text-relatable">relatable.</span>
        </p>
        <div className="hidden md:flex flex-col items-end gap-1.5 pb-1">
          <span className="eyebrow text-fog/60 text-[10px] tracking-[0.34em]">REEL 001 · 35 MM</span>
          <span className="font-mono text-[11px] tracking-[0.22em] text-fog/50">
            <span className="text-bone">300+</span>
            <span className="mx-2 text-fog/30">BRANDS</span>
            <span className="text-bone">2020 — {new Date().getFullYear()}</span>
          </span>
        </div>
      </div>

      <FilmStrip items={rowA} direction="left" rowIndex={0} />
      {useTwoRows && rowB.length > 0 && (
        <div className="mt-10 md:mt-14">
          <FilmStrip items={rowB} direction="right" rowIndex={1} />
        </div>
      )}
    </section>
  );
}

/**
 * A single moving reel: perforation rails top + bottom, and a duplicated
 * track of frames in between.
 */
function FilmStrip({ items, direction = "left", rowIndex = 0 }) {
  // ~1.4s per frame with an 18s floor — comfortable reading pace, each mark
  // gets ~2s in the sightline as it drifts through the viewport.
  const seconds = Math.max(18, Math.round(items.length * 1.4));
  return (
    <div className="film-strip relative w-full overflow-hidden">
      <SprocketRail direction={direction} seconds={seconds} />

      <div className="marquee-mask relative overflow-hidden">
        <div
          className="film-reel flex w-max items-stretch whitespace-nowrap will-change-transform"
          style={{
            animation: `${direction === "right" ? "marqueeRight" : "marqueeLeft"} ${seconds}s linear infinite`,
          }}
        >
          {[0, 1].map((copy) => (
            <div key={copy} className="flex items-stretch shrink-0" aria-hidden={copy === 1 ? "true" : undefined}>
              {items.map((c, i) => (
                <FilmFrame
                  key={`${copy}-${c.name}`}
                  client={c}
                  index={rowIndex * items.length + i}
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      <SprocketRail direction={direction} seconds={seconds} />
    </div>
  );
}

/**
 * Rail of punched perforations — rounded-rect holes (KS-perf shape) rendered
 * as a repeating SVG, scrolled with the reel so it feels physically attached.
 */
function SprocketRail({ direction = "left", seconds = 20 }) {
  return (
    <div
      className="sprocket-rail"
      aria-hidden="true"
      style={{
        animation: `${direction === "right" ? "sprocketRight" : "sprocketLeft"} ${seconds / 4}s linear infinite`,
      }}
    />
  );
}

/**
 * Aspect-aware sizing — the logo's natural aspect ratio drives the frame
 * width so square marks get square-ish frames and wordmarks get room.
 *
 * NOTE: The ratio is cached on a module-level Map keyed by logo URL. Both
 * duplicated copies of the reel share the same cached value, so they always
 * render at identical widths — otherwise, each FilmFrame instance would
 * resolve its ratio asynchronously and the two halves would briefly disagree,
 * which visibly breaks the -50% seamless loop.
 */
const ASPECT_CACHE = new Map();
const ASPECT_LISTENERS = new Map();

function useCardAspect(logo) {
  const cached = ASPECT_CACHE.get(logo);
  const [ratio, setRatio] = React.useState(cached ?? 3);
  React.useEffect(() => {
    if (ASPECT_CACHE.has(logo)) {
      setRatio(ASPECT_CACHE.get(logo));
      return undefined;
    }
    // Register a listener so every mounted frame using this logo updates
    // together the moment the image resolves — keeps duplicated copies in sync.
    let listeners = ASPECT_LISTENERS.get(logo);
    if (!listeners) {
      listeners = new Set();
      ASPECT_LISTENERS.set(logo, listeners);
      const img = new Image();
      img.onload = () => {
        if (img.naturalHeight > 0) {
          const r = img.naturalWidth / img.naturalHeight;
          ASPECT_CACHE.set(logo, r);
          listeners.forEach((fn) => fn(r));
          ASPECT_LISTENERS.delete(logo);
        }
      };
      img.src = logo;
    }
    listeners.add(setRatio);
    return () => {
      listeners.delete(setRatio);
    };
  }, [logo]);
  return ratio;
}

/** One frame — white logo on the film base, edge-print credit below. */
function FilmFrame({ client, index }) {
  const { name, logo } = client;
  const ratio = useCardAspect(logo);
  const frameNo = String(23 + index * 4).padStart(4, "0");
  const cardWidth = Math.min(320, Math.max(140, Math.round(112 * ratio)));

  return (
    <div
      data-testid={`trusted-by-logo-${name.replace(/\s+/g, "-").toLowerCase()}`}
      className="film-frame group shrink-0 flex flex-col items-stretch px-2.5 md:px-3"
      style={{ width: `${cardWidth}px` }}
      title={name}
    >
      {/* Exposed frame — uniform white mark, no matting */}
      <div className="film-frame-card relative overflow-hidden h-24 md:h-28 lg:h-32 flex items-center justify-center px-5 md:px-6">
        <img
          src={logo}
          alt={name}
          loading="lazy"
          decoding="async"
          className="film-frame-img select-none max-h-[68%] max-w-full w-auto h-auto object-contain"
          draggable={false}
        />
      </div>
      {/* Amber edge print — like burned-in stock markings */}
      <div className="film-frame-slug mt-1.5 flex items-center justify-between gap-2 px-1 font-mono text-[9px] tracking-[0.18em]">
        <span className="truncate uppercase edge-print">{name}</span>
        <span className="shrink-0 edge-print opacity-60">{frameNo} ▸</span>
      </div>
    </div>
  );
}
